import type { ReactNode, SVGProps } from 'react';
import { cn } from './utils';

/**
 * Conjunto de ícones próprio, em SVG inline.
 *
 * ACESSIBILIDADE: todo ícone nasce `aria-hidden` e `focusable="false"`. Ícone
 * é decoração; o significado tem que estar no texto ao lado. Quando um ícone
 * PRECISA carregar significado (ex.: o semáforo de validade), quem o usa
 * fornece um `<span class="qc-sr-only">` com o texto — nunca `aria-label` no
 * SVG, porque leitor de tela em modo de navegação por elementos pode pular.
 */
export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  readonly size?: number;
}

function createIcon(displayName: string, path: ReactNode, viewBox = '0 0 24 24') {
  const Icon = ({ size = 16, className, ...props }: IconProps) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn('shrink-0', className)}
      {...props}
    >
      {path}
    </svg>
  );
  Icon.displayName = displayName;
  return Icon;
}

/**
 * Marca da QualiCloud.
 *
 * É o vetor OFICIAL da empresa — o mesmo `favicon.svg` do site institucional,
 * que já é a redução da nuvem do logotipo completo. Acrescentar a lâmpada do
 * logotipo grande virou um borrão em 26 px: a redução existe justamente porque
 * a empresa já resolveu esse problema.
 *
 * Herda `currentColor`, então acompanha o token da marca nos dois temas —
 * petróleo no claro, turquesa no escuro. Um PNG com a cor cravada ficaria
 * ilegível sobre a superfície escura.
 */
export const IconQualiCloud = createIcon(
  'IconQualiCloud',
  <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />,
);

export const IconCheck = createIcon('IconCheck', <path d="m4 12.5 5 5L20 6.5" />);

export const IconClose = createIcon('IconClose', <path d="M6 6l12 12M18 6 6 18" />);

export const IconChevronDown = createIcon('IconChevronDown', <path d="m6 9 6 6 6-6" />);

export const IconChevronUp = createIcon('IconChevronUp', <path d="m6 15 6-6 6 6" />);

export const IconChevronRight = createIcon('IconChevronRight', <path d="m9 6 6 6-6 6" />);

export const IconChevronLeft = createIcon('IconChevronLeft', <path d="m15 6-6 6 6 6" />);

export const IconChevronsLeft = createIcon(
  'IconChevronsLeft',
  <>
    <path d="m11 6-6 6 6 6" />
    <path d="m18 6-6 6 6 6" />
  </>,
);

export const IconChevronsRight = createIcon(
  'IconChevronsRight',
  <>
    <path d="m13 6 6 6-6 6" />
    <path d="m6 6 6 6-6 6" />
  </>,
);

export const IconSearch = createIcon(
  'IconSearch',
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.6-3.6" />
  </>,
);

/** Vencido — semáforo vermelho. Forma distinta de todos os outros. */
export const IconAlertOctagon = createIcon(
  'IconAlertOctagon',
  <>
    <path d="M8.3 3h7.4L21 8.3v7.4L15.7 21H8.3L3 15.7V8.3z" />
    <path d="M12 8v4.5" />
    <path d="M12 16h.01" />
  </>,
);

/** Vencendo — semáforo amarelo. Triângulo: forma diferente do octógono. */
export const IconAlertTriangle = createIcon(
  'IconAlertTriangle',
  <>
    <path d="M10.3 3.9 2.4 17.6a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </>,
);

/** Válido — semáforo verde. Círculo com visto. */
export const IconCheckCircle = createIcon(
  'IconCheckCircle',
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="m8.5 12.3 2.4 2.4 4.6-4.9" />
  </>,
);

/** Sem validade — semáforo cinza. Traço: ausência, não estado. */
export const IconMinusCircle = createIcon(
  'IconMinusCircle',
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12h7" />
  </>,
);

export const IconInfo = createIcon(
  'IconInfo',
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </>,
);

export const IconShieldCheck = createIcon(
  'IconShieldCheck',
  <>
    <path d="M12 3 4.5 6v6c0 4.4 3.1 8.2 7.5 9 4.4-.8 7.5-4.6 7.5-9V6z" />
    <path d="m9 12 2.2 2.2L15.5 10" />
  </>,
);

export const IconSignature = createIcon(
  'IconSignature',
  <>
    <path d="M3 18c3.2 0 3.4-12 6.2-12 2 0 1.4 8.4 3.6 8.4 1.6 0 1.9-4.2 3.5-4.2 1.2 0 1.3 2.6 2.6 2.6" />
    <path d="M3 21h18" />
  </>,
);

export const IconLock = createIcon(
  'IconLock',
  <>
    <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
    <path d="M8 10.5V7.8a4 4 0 1 1 8 0v2.7" />
  </>,
);

export const IconKey = createIcon(
  'IconKey',
  <>
    <circle cx="8" cy="15" r="4" />
    <path d="m10.9 12.1 8-8" />
    <path d="m17 6 2 2" />
    <path d="m14.5 8.5 2 2" />
  </>,
);

export const IconUser = createIcon(
  'IconUser',
  <>
    <circle cx="12" cy="8" r="4" />
    <path d="M4.5 20c0-3.6 3.4-6 7.5-6s7.5 2.4 7.5 6" />
  </>,
);

export const IconUsers = createIcon(
  'IconUsers',
  <>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 19.5c0-3.2 2.9-5.5 6.5-5.5s6.5 2.3 6.5 5.5" />
    <path d="M16.5 5.2a3.5 3.5 0 0 1 0 6.6" />
    <path d="M18 14.3c2.1.7 3.5 2.4 3.5 4.6" />
  </>,
);

export const IconBuilding = createIcon(
  'IconBuilding',
  <>
    <path d="M4 21V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v16" />
    <path d="M15 9h3a2 2 0 0 1 2 2v10" />
    <path d="M8 7h3M8 11h3M8 15h3" />
    <path d="M2.5 21h19" />
  </>,
);

export const IconFile = createIcon(
  'IconFile',
  <>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </>,
);

export const IconFiles = createIcon(
  'IconFiles',
  <>
    <path d="M15 2H8a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V5z" />
    <path d="M15 2v4h4" />
    <path d="M3.5 7v13a2 2 0 0 0 2 2H14" />
  </>,
);

export const IconFolder = createIcon(
  'IconFolder',
  <path d="M3 7a2 2 0 0 1 2-2h4.2l2 2.4H19a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
);

export const IconFolderOpen = createIcon(
  'IconFolderOpen',
  <>
    <path d="M3 8V7a2 2 0 0 1 2-2h4.2l2 2.4H19a2 2 0 0 1 2 2v.6" />
    <path d="M3.2 10h18a1 1 0 0 1 .97 1.24l-1.7 7A2 2 0 0 1 18.5 20H5.2a2 2 0 0 1-2-2z" />
  </>,
);

export const IconUpload = createIcon(
  'IconUpload',
  <>
    <path d="M12 16V4" />
    <path d="m7.5 8.5 4.5-4.5 4.5 4.5" />
    <path d="M4 16v2.5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V16" />
  </>,
);

export const IconDownload = createIcon(
  'IconDownload',
  <>
    <path d="M12 4v12" />
    <path d="m7.5 11.5 4.5 4.5 4.5-4.5" />
    <path d="M4 16v2.5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V16" />
  </>,
);

export const IconPrinter = createIcon(
  'IconPrinter',
  <>
    <path d="M7 9V3.5h10V9" />
    <path d="M7 18H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2" />
    <rect x="7" y="14.5" width="10" height="6" rx="1" />
  </>,
);

export const IconBell = createIcon(
  'IconBell',
  <>
    <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16S18 14 18 9" />
    <path d="M13.7 19.5a2 2 0 0 1-3.4 0" />
  </>,
);

export const IconSettings = createIcon(
  'IconSettings',
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 14.4a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.56 1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.88 1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.56-1.1 1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.88.34H9a1.7 1.7 0 0 0 1-1.56V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1.03 1.56 1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.88V9a1.7 1.7 0 0 0 1.56 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1.03" />
  </>,
);

export const IconDashboard = createIcon(
  'IconDashboard',
  <>
    <rect x="3" y="3" width="7.5" height="8.5" rx="1.5" />
    <rect x="13.5" y="3" width="7.5" height="5" rx="1.5" />
    <rect x="13.5" y="10.5" width="7.5" height="10.5" rx="1.5" />
    <rect x="3" y="14" width="7.5" height="7" rx="1.5" />
  </>,
);

/**
 * Indicadores.
 *
 * A tela de indicadores usava o ícone de engrenagem, que quer dizer
 * "configuração" em qualquer interface — e passou a colidir de frente com o
 * bloco Configurações do menu. Barras é o que a tela mostra.
 */
export const IconChartBar = createIcon(
  'IconChartBar',
  <>
    <path d="M4 20V4" />
    <path d="M4 20h16" />
    <rect x="7.5" y="12" width="3" height="5" rx="0.5" />
    <rect x="12.5" y="8.5" width="3" height="8.5" rx="0.5" />
    <rect x="17.5" y="5" width="3" height="12" rx="0.5" />
  </>,
);

export const IconHistory = createIcon(
  'IconHistory',
  <>
    <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" />
    <path d="M3.5 4.5V10h5.5" />
    <path d="M12 7.8V12l3 1.8" />
  </>,
);

export const IconClipboardCheck = createIcon(
  'IconClipboardCheck',
  <>
    <path d="M9 4H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2" />
    <rect x="9" y="2.5" width="6" height="3.5" rx="1" />
    <path d="m9.2 13.2 2 2 3.6-4" />
  </>,
);

export const IconSortAsc = createIcon(
  'IconSortAsc',
  <>
    <path d="M12 19V5" />
    <path d="m6.5 10.5 5.5-5.5 5.5 5.5" />
  </>,
);

export const IconSortDesc = createIcon(
  'IconSortDesc',
  <>
    <path d="M12 5v14" />
    <path d="m6.5 13.5 5.5 5.5 5.5-5.5" />
  </>,
);

export const IconSortNeutral = createIcon(
  'IconSortNeutral',
  <>
    <path d="m8 9.5 4-4 4 4" />
    <path d="m8 14.5 4 4 4-4" />
  </>,
);

export const IconFilter = createIcon(
  'IconFilter',
  <path d="M3.5 5h17l-6.6 7.8V19l-3.8 2v-8.2z" />,
);

export const IconPlus = createIcon(
  'IconPlus',
  <>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </>,
);

export const IconTrash = createIcon(
  'IconTrash',
  <>
    <path d="M4 7h16" />
    <path d="M9.5 7V5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v2" />
    <path d="M6.5 7l.8 12a2 2 0 0 0 2 1.9h5.4a2 2 0 0 0 2-1.9L17.5 7" />
  </>,
);

export const IconEdit = createIcon(
  'IconEdit',
  <>
    <path d="M4 20h4.2L19.5 8.7a2.1 2.1 0 0 0-3-3L5.2 17z" />
    <path d="m15.5 6.2 3 3" />
  </>,
);

export const IconEye = createIcon(
  'IconEye',
  <>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12" />
    <circle cx="12" cy="12" r="3" />
  </>,
);

export const IconEyeOff = createIcon(
  'IconEyeOff',
  <>
    <path d="M9.9 5.8A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-3 3.9" />
    <path d="M6.3 7.7A17 17 0 0 0 2.5 12S6 18.5 12 18.5a9.4 9.4 0 0 0 3.9-.83" />
    <path d="M10 10a2.8 2.8 0 0 0 4 4" />
    <path d="m3.5 3.5 17 17" />
  </>,
);

export const IconLogout = createIcon(
  'IconLogout',
  <>
    <path d="M14 4.5h3.5a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H14" />
    <path d="M10 8.5 6.5 12l3.5 3.5" />
    <path d="M6.5 12H15" />
  </>,
);

export const IconArrowLeft = createIcon(
  'IconArrowLeft',
  <>
    <path d="M19 12H5" />
    <path d="m10.5 6.5-5.5 5.5 5.5 5.5" />
  </>,
);

export const IconMenu = createIcon(
  'IconMenu',
  <>
    <path d="M4 7h16" />
    <path d="M4 12h16" />
    <path d="M4 17h16" />
  </>,
);

export const IconMoon = createIcon(
  'IconMoon',
  <path d="M20.5 14.2A8.5 8.5 0 0 1 9.8 3.5a8.5 8.5 0 1 0 10.7 10.7" />,
);

export const IconSun = createIcon(
  'IconSun',
  <>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2.5v2M12 19.5v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2.5 12h2M19.5 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
  </>,
);

export const IconClock = createIcon(
  'IconClock',
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5.3l3.4 2" />
  </>,
);

export const IconCalendar = createIcon(
  'IconCalendar',
  <>
    <rect x="3.5" y="5" width="17" height="16" rx="2" />
    <path d="M3.5 10h17" />
    <path d="M8 3v4M16 3v4" />
  </>,
);

export const IconLink = createIcon(
  'IconLink',
  <>
    <path d="M10 13.5a4 4 0 0 0 5.7.3l2.6-2.6a4 4 0 0 0-5.66-5.66l-1.5 1.5" />
    <path d="M14 10.5a4 4 0 0 0-5.7-.3l-2.6 2.6a4 4 0 0 0 5.66 5.66l1.5-1.5" />
  </>,
);

export const IconGrip = createIcon(
  'IconGrip',
  <>
    <circle cx="9" cy="6" r="1.2" fill="currentColor" />
    <circle cx="15" cy="6" r="1.2" fill="currentColor" />
    <circle cx="9" cy="12" r="1.2" fill="currentColor" />
    <circle cx="15" cy="12" r="1.2" fill="currentColor" />
    <circle cx="9" cy="18" r="1.2" fill="currentColor" />
    <circle cx="15" cy="18" r="1.2" fill="currentColor" />
  </>,
);

export const IconInbox = createIcon(
  'IconInbox',
  <>
    <path d="M3.5 13.5h4l1.3 2.6h6.4l1.3-2.6h4" />
    <path d="M6 4.5h12l3 9v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4z" />
  </>,
);

export const IconExternal = createIcon(
  'IconExternal',
  <>
    <path d="M13.5 4.5H19.5V10.5" />
    <path d="M19.5 4.5 11 13" />
    <path d="M18 14v4.5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4.5" />
  </>,
);

export const IconRefresh = createIcon(
  'IconRefresh',
  <>
    <path d="M20.5 12a8.5 8.5 0 1 1-2.5-6" />
    <path d="M20.5 3.5V9H15" />
  </>,
);
